import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { ArrowLeft, User, TrendingUp, Award, AlertCircle, Sparkles, Brain, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClassDetail() {
  const { classId, subdomain } = useParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const basePath = subdomain ? `/${subdomain}/admin` : '/admin';

  useEffect(() => {
    fetchStudents();
    fetchAiAnalysis();
  }, [classId]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/admin/classes/${classId}/students`, {
        headers: getAuthHeaders(),
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAnalysis = async () => {
    setLoadingAi(true);
    try {
      const response = await axios.get(`${API}/admin/ai-analysis/class/${classId}`, {
        headers: getAuthHeaders(),
      });
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      setAiAnalysis(null);
    } finally {
      setLoadingAi(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Жүктелуде...</div>;
  }

  const totalMerit = students.reduce((sum, s) => sum + s.merit_count, 0);
  const totalDemerit = students.reduce((sum, s) => sum + s.demerit_count, 0);
  const avgScore = students.length > 0 
    ? (students.reduce((sum, s) => sum + s.total_score, 0) / students.length).toFixed(1)
    : 0;

  return (
    <div className="space-y-6 fade-in" data-testid="class-detail">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/admin')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-3xl font-bold heading-font tracking-tight">
            {students[0]?.class_name || 'Сынып'}
          </h2>
          <p className="text-muted">{students.length} оқушы</p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold heading-font ${avgScore >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {avgScore >= 0 ? '+' : ''}{avgScore}
          </div>
          <p className="text-xs text-muted">Орташа ұпай</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <Award className="w-6 h-6 text-green-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-green-600">{totalMerit}</div>
            <p className="text-xs text-muted">Жалпы мақтау</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-red-600">{totalDemerit}</div>
            <p className="text-xs text-muted">Жалпы сөгіс</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <User className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold">{students.length}</div>
            <p className="text-xs text-muted">Оқушы саны</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Analysis Card */}
      <Card className="paper-card border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-purple-700">
            <Sparkles className="w-5 h-5" />
            AI Сынып талдауы
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingAi ? (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mr-3" />
              <span className="text-purple-600">AI талдау жасалуда...</span>
            </div>
          ) : aiAnalysis ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                <p className="text-gray-700 leading-relaxed">{aiAnalysis}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchAiAnalysis}
                className="text-purple-600 hover:text-purple-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Қайта талдау
              </Button>
            </div>
          ) : (
            <p className="text-muted text-center py-4">
              AI талдау қолжетімсіз
            </p>
          )}
        </CardContent>
      </Card>

      {/* Students List */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Оқушылар тізімі</h3>
        {students.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted">Оқушылар табылмады</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {students.map((student, index) => (
              <Card
                key={student.student_id}
                className="paper-card cursor-pointer hover:shadow-lg transition-all"
                onClick={() => navigate(`${basePath}/student/${student.student_id}`)}
                data-testid={`student-card-${student.student_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 
                        index === 2 ? 'bg-amber-600' : 'bg-primary/60'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{student.student_name}</h3>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-sm text-muted">
                            <span className="merit-badge">{student.merit_count} Марапат</span>
                          </span>
                          <span className="text-sm text-muted">
                            <span className="demerit-badge">{student.demerit_count} Ескерту</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className={`text-3xl font-bold heading-font score-animation ${
                        student.total_score >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {student.total_score >= 0 ? '+' : ''}{student.total_score}
                      </div>
                      <p className="text-xs text-muted">Ұпай</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
